import { RestError } from '@azure/core-http';
import { Container } from '@mui/material';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { createPath, Paths, ProjectPathParams } from '../../../app/router/app.router';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { ListPageHeader } from '../../../ui/components/pure/page-headers/list/list-page-header.component';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { AddCarrierModal } from './actions/add-carrier/add-carrier-action.component';
import { CarriersTable } from './components/carriers-list-table.component';
import { useCarriersList } from './hooks/use-carriers-list.hook';
import { makeCarriersEmptyMessage, makeCarriersListPageHeaderProps } from './logic/carriers-list-props.map';

export const CarriersListPage = ({
  params,
  pending = false
}: {
  params: ProjectPathParams<Paths.carriers>;
  pending?: boolean;
}) => {
  const history = useHistory();
  const {
    project,
    result: carriers,
    infiniteScroll,
    formData,
    setFormData
  } = useCarriersList(params.projectID, pending);
  const title = pending ? 'Pending Carriers' : 'Carriers';
  const [adding, setAdding] = useState(false);

  const sortData = useMemo(
    () => ({ sortBy: formData.sortBy, sortOrder: formData.sortOrder }),
    [formData.sortBy, formData.sortOrder]
  );
  const error = project.error || carriers.error;

  if (error) {
    if ((error as RestError).statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" />;
    }

    return <ErrorFullPage />;
  }

  const headerProps = makeCarriersListPageHeaderProps(
    params.projectID,
    carriers.data?.pages[0].totalRecords || 0,
    formData,
    setFormData,
    () => setAdding(true),
    title
  );

  return (
    <Container>
      {project.data && <WindowTitle detail={project.data.name} pageTitle={title} />}
      <ListPageHeader {...headerProps} />
      <CarriersTable
        params={params}
        pages={carriers.data?.pages || []}
        infiniteScroll={infiniteScroll}
        setFormData={setFormData}
        sortData={sortData}
        isLoading={carriers.isLoading}
        isPreviousData={carriers.isPreviousData}
        hasError={carriers.isError}
        customMessage={makeCarriersEmptyMessage(formData)}
      />
      {adding && (
        <AddCarrierModal
          projectID={params.projectID}
          onSuccess={(carrier) =>
            history.push(
              createPath(Paths.carrier, { projectID: params.shortcode || params.projectID, carrierID: carrier.id })
            )
          }
          onAbort={() => setAdding(false)}
        />
      )}
    </Container>
  );
};
